<template>
  <div class="product-items-container" v-hotkey="keymap">
    <slot name="header" />
    <table
      class="product-items-table"
      cellpadding="0"
      cellspacing="0"
    >
      <table-header
        class="product-items-table__header"
        :enabled-columns="enabledColumns"
        :is-vat-zero="isVatZero"
      />
      <table-body
        v-if="items.length"
        ref="table-body"
        :enabled-elements="enabledElements"
        :enabled-columns="enabledColumns"
        :auto-select-stock="autoSelectStock"
        :currency="currency"
        :custom-prices="customPrices"
        :price-type="priceType"
        :min-count="minCount"
        :compare-items="compareItems"
        :default-supplier="defaultSupplier"
        :compare-type="compareType"
        :items="items"
        :is-vat-zero="isVatZero"
        :is-product-select-from-stock="isProductSelectFromStock"
        :available-products="availableProducts"
        :load-base-count-from-stock="loadBaseCountFromStock"
        :storage-id="storageId"
        :round-net="roundNet"
        :show-row-warning="showRowWarning"
        class="product-items-table__body"
        @priceChange="onPriceChange"
      />
    </table>
    <add-row
      v-if="displayActions"
      :disabled="!enabledElements.includes('addButton') || disabled || defaultProductDataLoading"
      :display-add-service="displayAddService"
      :display-add-free-word-item="displayAddFreeWordItem"
      @addProduct="onAddProduct"
      @addService="onAddService"
      @addFreeWordItem="onAddFreeWordItem"
    />
    <slot name="footer" />
  </div>
</template>
<script>
import TableHeader from './TableHeader';
import TableBody from './TableBody';
import AddRow from './AddRow';
import './style.scss';

export default {
  components: {
    TableHeader,
    TableBody,
    AddRow,
  },
  props: {
    defaultItems: {
      type: Array,
      default() {
        return [];
      },
      required: true,
    },
    disabled: {
      type: Boolean,
      default() {
        return false;
      },
      required: false
    },
    currency: {
      type: String,
      default() {
        return 'HUF';
      },
      required: false,
    },
    minCount: {
      type: Number,
      default() {
        return 1;
      },
      required: false,
    },
    autoSelectStock: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
    appendToProducts: {
      type: Object,
      default() {
        return {};
      },
      required: false,
    },
    defaultSupplier: {
      type: Object,
      default() {
        return null;
      },
      required: false,
    },
    compareItems: {
      type: Array,
      default() {
        return null;
      },
      required: false,
    },
    priceType: {
      type: String,
      default() {
        return 'purchase_price';
      },
      required: false,
    },
    customPrices: {
      type: Array,
      default() {
        return [];
      },
      required: false,
    },
    compareType: {
      type: String,
      default() {
        return 'diff';
      },
      required: false,
    },
    enabledElements: {
      type: Array,
      default() {
        return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice', 'expirationDate', 'expirationDateSelector', 'supplierSelector'];
      },
      required: false,
    },
    enabledColumns: {
      type: Array,
      default() {
        return ['product', 'productPackType', 'expirationDate', 'supplierSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'actions'];
      },
      required: false,
    },
    isVatZero: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
    isProductSelectFromStock: {
      type: Boolean,
      default() {
        return true;
      },
      required: false,
    },
    loadBaseCountFromStock: {
      type: Boolean,
      default() {
        return true;
      },
      required: false,
    },
    displayActions: {
      type: Boolean,
      default() {
        return true;
      },
      required: false,
    },
    displayAddService: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
    displayAddFreeWordItem: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
    roundNet: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
    availableProducts: {
      validator: prop => typeof prop === 'object' || prop === null,
      default() {
        return null;
      },
      required: false,
    },
    storageId: {
      type: Number,
      default() {
        return 9;
      }
    },
    showRowWarning: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
  },
  data() {
    return {
      items: [],
    };
  },
  watch: {
    defaultItems(items) {
      this.items = JSON.parse(JSON.stringify(items.map(item => ({
        ...item,
        type: (item.service_id || (item.service && item.service.id)) ? 'service' : ((item.product_id || (item.product && item.product.id)) ? 'product' : 'free-word'),
      }))));
    },
  },
  mounted() {
    this.$store.dispatch('product/getDefaultProductData')

    if (!this.$store.getters['vat/vats'] || !this.$store.getters['vat/vats'].length) {
      this.$store.dispatch('vat/fetchAll');
    }
    this.items = JSON.parse(JSON.stringify(this.defaultItems.map(item => ({
      ...item,
      type: (item.service_id || (item.service && item.service.id)) ? 'service' : ((item.product_id || (item.product && item.product.id)) ? 'product' : 'free-word'),
    }))));
  },
  computed: {
    keymap () {
      return {
        'shift+t': this.onAddProduct,
        // 'enter': {
        //   keydown: this.hide,
        //   keyup: this.show
        // }
      }
    },
    defaultProductDataLoading() {
      return this.$store.getters['product/defaultProductDataLoading']
    }
  },
  methods: {
    clearItems() {
      this.items = [];
    },
    getItems() {
      if (this.$refs['table-body']) {
        return this.$refs['table-body'].getItems();
      }
    },
    validateExpirationDates() {
      return this.$refs['table-body'].validateExpirationDates();
    },
    validateSuppliers() {
      return this.$refs['table-body'].validateSuppliers();
    },
    validateProducts() {
      let valid = true;
      this.getItems().forEach(item => {
        if (
          (item.type === 'product' && (item.unit_price === undefined || item.net_price === undefined || !item.product || !item.product.id || !item.product_pack_type || !item.product_pack_type.id)) ||
          (item.type === 'service' && (item.unit_price === undefined || item.net_price === undefined || !item.service || !item.service.id)) ||
          (item.type === 'free-word' && (item.unit_price === undefined || item.net_price === undefined || !item.product_name))
        ) {
          valid = false;
        }
      });
      return valid;
    },
    onAddProduct() {
      this.items.push({
        type: 'product',
        pack_type_name: 'karton',
        product: {
          suppliers: [],
        },
        product_pack_type: {},
        count: 1,
        unit_price: 0,
        net_price: 0,
        vat: this.isVatZero ? 0 : null,
        declutter_reason: 'lejárt szavidő',
        supplier_id: null,
        base_count: 0,
        comment: '',
        ...this.appendToProducts,
      });
      setTimeout(() => {
        if (
          this.$refs['table-body'] &&
          this.$refs['table-body'].$refs &&
          this.$refs['table-body'].$refs['row']
        ) {
          const rows = this.$refs['table-body'].$refs['row'];
          if (
            rows[rows.length - 1] &&
            rows[rows.length - 1].$refs &&
            rows[rows.length - 1].$refs['product-selector'] &&
            rows[rows.length - 1].$refs['product-selector'].$refs &&
            rows[rows.length - 1].$refs['product-selector'].$refs['product-selector-input'] &&
            rows[rows.length - 1].$refs['product-selector'].$refs['product-selector-input'].focus
          ) {
            rows[rows.length - 1].$refs['product-selector'].$refs['product-selector-input'].focus();
          }
        }
      }, 100)
    },
    onAddService() {
      this.items.push({
        type: 'service',
        service: {},
        count: 1,
        unit_price: 0,
        net_price: 0,
        vat: this.isVatZero ? 0 : 27,
        comment: '',
        ...this.appendToProducts,
      });
    },
    onAddFreeWordItem() {
      this.items.push({
        product_name: '',
        type: 'free-word',
        unit_of_measure: 'PIECE',
        count: 1,
        unit_price: 0,
        net_price: 0,
        vat: this.isVatZero ? 0 : 27,
        comment: '',
      });
    },
    onPriceChange() {
      const items = this.$refs['table-body'].getItems();
      const netPrice = items.reduce((acc, curr) => acc + curr.net_price, 0);
      const grossPrice = items.reduce((acc, curr) => acc + (curr.net_price * (curr.vat / 100 + 1)), 0);
      this.$emit('priceChange', {
        netPrice,
        grossPrice,
      });
    },
  },
};
</script>
